import styled from "styled-components";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { format } from "date-fns";
import AdminService from "../../../services/admin.service";
import { useGlobal } from "../../../context/global";
import { AccountBalance } from "../../../types/common.types";
import formatNumberToString from "../../../utils/formatNumber";
import removeTimezone from "../../../utils/removeTimezone";

interface BalancesProps {
    day?: Date
    endDay?: Date
}

function ConfigureOpeningBalance({ account, onSaved }: { account: AccountBalance, onSaved: () => void }) {
    const { setIsLoading } = useGlobal()
    const [amount, setAmount] = useState('')
    const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'))

    async function submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        try {
            await AdminService.setAccountOpeningBalance(account.id, Number(amount), new Date(date))
            onSaved()
        } catch (error: any) {
            alert(typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo configurar el saldo inicial')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <form className="configure" onSubmit={submit}>
            <p className="hint">Aún no tiene saldo inicial. Se configura una sola vez — después no se puede editar desde aquí.</p>
            <div className="fields">
                <input type="number" placeholder="Saldo inicial" required value={amount} onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)} />
                <input type="date" required value={date} onChange={(e: ChangeEvent<HTMLInputElement>) => setDate(e.target.value)} />
                <button type="submit">Configurar</button>
            </div>
        </form>
    )
}

export default function Balances({ day, endDay }: BalancesProps) {
    const { setIsLoading } = useGlobal()
    const [accounts, setAccounts] = useState<AccountBalance[]>([])

    async function load() {
        setIsLoading(true)
        try {
            const res = await AdminService.getAccountBalances(day, endDay)
            setAccounts(res.data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day, endDay])

    return (
        <Main>
            <h3>Saldos por cuenta</h3>
            {accounts.map(account => (
                <div className="card" key={account.id}>
                    <div className="header">
                        <span className="name">{account.name}</span>
                        {account.opening_balance_date && (
                            <span className="saldo">Saldo actual: ${formatNumberToString(account.saldo_actual || 0)}</span>
                        )}
                    </div>
                    {account.opening_balance_date ? (
                        <div className="details">
                            <span>Saldo inicial: ${formatNumberToString(account.opening_balance)} ({removeTimezone(account.opening_balance_date)})</span>
                            {(day) && (
                                <span>Movimiento del período: ingresos ${formatNumberToString(account.period_ingresos)} · egresos ${formatNumberToString(account.period_egresos)}</span>
                            )}
                        </div>
                    ) : (
                        <ConfigureOpeningBalance account={account} onSaved={load} />
                    )}
                </div>
            ))}
        </Main>
    )
}

const Main = styled.div`
margin: 20px 0;
h3 { font-size: 16px; margin-bottom: 10px; }
.card {
    padding: 12px;
    border-radius: 8px;
    background: ${props => props.theme.whiteBackground};
    margin-bottom: 10px;
}
.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    .name { font-weight: 700; }
    .saldo { font-weight: 700; color: ${props => props.theme.blue}; }
}
.details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 6px;
    font-size: 13px;
    color: ${props => props.theme.gray};
}
.configure {
    margin-top: 8px;
    .hint {
        font-size: 12px;
        color: ${props => props.theme.gray};
        margin-bottom: 6px;
    }
    .fields {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        input {
            padding: 6px 8px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 13px;
        }
        button {
            padding: 6px 12px;
            border-radius: 6px;
            border: 2px solid ${props => props.theme.blue};
            background: transparent;
            color: ${props => props.theme.blue};
            cursor: pointer;
        }
    }
}
`
